/* External */
import {
  memo,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import { ScrollContext, useScrollPixels } from './ScrollContext';
import { START, END, CENTER } from './ScrollPosition';
import { ALWAYS, IF_NEEDED } from './ScrollMode';
import { isFullyVisible } from '../../utils/dom';

/* Styles */
import './ScrollView.scss';

const ScrollView = forwardRef(({
  className = '',
  style,
  scrollableStyle,
  onScroll,
  children,
  resetScrollPosition = false,
  propagateEventOnReachTop = false,
  initialScrollPosition = 0,
  initialMaxOffset = 0,
  smoothScrollEnabled = false,
  smoothScrollFactor = 0.18,
}, ref) => {
  const [offset, setOffset] = useState(initialScrollPosition);

  const scrollViewRef = useRef();
  const scrollableRef = useRef();
  const scrollData = useRef();

  const animationFrameRef = useRef(null);
  const currentOffsetRef = useRef(initialScrollPosition);
  const targetOffsetRef = useRef(initialScrollPosition);

  const getMaxOffset = useCallback(() => {
    let newMaxOffset = (scrollableRef.current.clientHeight - scrollViewRef.current.clientHeight)
      + (scrollableRef.current.offsetTop + scrollViewRef.current.offsetTop);

    if (initialMaxOffset) {
      newMaxOffset = initialMaxOffset;
    } else {
      newMaxOffset = newMaxOffset < 0 ? 0 : newMaxOffset;
    }

    return newMaxOffset;
  }, [initialMaxOffset]);

  const clampOffset = useCallback((value) => {
    const maxOffset = getMaxOffset();
    if (value < 0) return 0;
    if (value > maxOffset) return maxOffset;
    return value;
  }, [getMaxOffset]);

  const applyOffset = useCallback((value) => {
    if (!scrollableRef.current) return;
    scrollableRef.current.style.transform = `translateY(-${value}px)`;
  }, []);

  const stopAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  const animateToTarget = useCallback(() => {
    stopAnimation();

    const step = () => {
      const { current } = currentOffsetRef;
      const target = targetOffsetRef.current;
      const delta = target - current;

      if (Math.abs(delta) < 0.5) {
        currentOffsetRef.current = target;
        applyOffset(target);
        setOffset(target);
        animationFrameRef.current = undefined;
        return;
      }

      const next = current + (delta * smoothScrollFactor);
      currentOffsetRef.current = next;
      applyOffset(next);
      setOffset(next);

      animationFrameRef.current = requestAnimationFrame(step);
    };

    animationFrameRef.current = requestAnimationFrame(step);
  }, [applyOffset, smoothScrollFactor, stopAnimation]);

  const setScrollOffset = useCallback((newOffset, immediate = false) => {
    const clampedOffset = clampOffset(newOffset);
    targetOffsetRef.current = clampedOffset;

    if (!smoothScrollEnabled || immediate) {
      stopAnimation();
      currentOffsetRef.current = clampedOffset;
      applyOffset(clampedOffset);
      setOffset(clampedOffset);
      return;
    }

    animateToTarget();
  }, [applyOffset, animateToTarget, clampOffset, smoothScrollEnabled, stopAnimation]);

  const scrollIntoView = useCallback(
    (element, {
      position = START,
      mode = ALWAYS,
    }) => {
      scrollData.current = { element, position, mode };
      if (mode === IF_NEEDED && isFullyVisible(element)) return;

      const elementRect = (
        element.getBoundingClientRect && element.getBoundingClientRect()
      ) || element;
      const scrollableRect = scrollableRef.current.getBoundingClientRect();

      const elementRelativePosition = (elementRect.top - scrollableRect.top)
        + scrollableRef.current.offsetTop;
      const elementHeight = element.clientHeight || elementRect.height;
      const scrollViewHeight = scrollViewRef.current.clientHeight;

      let newOffset;

      switch (position) {
        case END: {
          newOffset = elementRelativePosition - (scrollViewHeight - elementHeight);
          break;
        }
        case CENTER: {
          newOffset = elementRelativePosition - (scrollViewHeight - elementHeight) / 2;
          break;
        }
        default: {
          newOffset = elementRelativePosition;
          break;
        }
      }

      setScrollOffset(newOffset);
    },
    [setScrollOffset],
  );

  const parentScrollPixels = useScrollPixels();

  const scrollPixels = useCallback((pixels, childScrollPixels) => {
    const currentBaseOffset = smoothScrollEnabled
      ? targetOffsetRef.current
      : currentOffsetRef.current;

    const maxOffset = getMaxOffset();
    let newOffset = currentBaseOffset + pixels;
    let remainingDelta = 0;

    if (newOffset < 0) {
      remainingDelta = newOffset;
      newOffset = 0;

      setTimeout(() => {
        parentScrollPixels(remainingDelta);
      }, 0);
    } else if (newOffset > maxOffset) {
      remainingDelta = newOffset - maxOffset;
      newOffset = maxOffset;

      if (childScrollPixels) {
        setTimeout(() => {
          childScrollPixels(remainingDelta);
        }, 0);
      }
    }

    setScrollOffset(newOffset);
  }, [getMaxOffset, parentScrollPixels, setScrollOffset, smoothScrollEnabled]);

  useEffect(() => {
    const clamped = clampOffset(currentOffsetRef.current);

    if (clamped !== currentOffsetRef.current) {
      setScrollOffset(clamped, true);
    }
  }, [clampOffset, getMaxOffset, setScrollOffset]);

  const scrollableFullStyle = useMemo(() => ({
    transform: `translateY(-${offset}px)`,
    willChange: smoothScrollEnabled ? 'transform' : undefined,
    ...scrollableStyle,
  }), [offset, scrollableStyle, smoothScrollEnabled]);

  const handleWheel = useCallback((event) => {
    const pixels = event.deltaY;

    if (!propagateEventOnReachTop
      || (propagateEventOnReachTop && offset !== 0 && pixels < 0)
      || pixels > 0) {
      event.stopPropagation();
    }

    if (pixels < 0) {
      scrollPixels(pixels);
    } else {
      parentScrollPixels(pixels, scrollPixels);
    }
  }, [offset, parentScrollPixels, propagateEventOnReachTop, scrollPixels]);

  const scrollToTop = useCallback(() => setScrollOffset(0), [setScrollOffset]);
  const scrollToBottom = useCallback(() => setScrollOffset(
    getMaxOffset(),
  ), [getMaxOffset, setScrollOffset]);

  useEffect(() => {
    if (onScroll) {
      onScroll(offset, getMaxOffset());
    }
  }, [getMaxOffset, offset, onScroll]);

  useEffect(() => {
    currentOffsetRef.current = offset;
  }, [offset]);

  const contextValue = useMemo(() => ({
    scrollPixels,
    scrollIntoView,
    scrollToTop,
    scrollToBottom,
  }), [scrollPixels, scrollIntoView, scrollToTop, scrollToBottom]);

  useEffect(() => {
    if (resetScrollPosition) setScrollOffset(0, true);
  }, [resetScrollPosition, setScrollOffset]);

  useEffect(() => () => {
    stopAnimation();
  }, [stopAnimation]);

  useImperativeHandle(ref, () => ({
    element: scrollViewRef.current,
    scrollToTop,
    scrollToBottom,
  }), [scrollToBottom, scrollToTop]);

  return (
    <div
      className={`dtv-html5-c-scroll-view ${className}`}
      style={style}
      onWheel={handleWheel}
      ref={scrollViewRef}
    >
      <div
        className="dtv-html5-c-scroll-view__scrollable"
        style={scrollableFullStyle}
        ref={scrollableRef}
      >
        <ScrollContext.Provider value={contextValue}>
          {children}
        </ScrollContext.Provider>
      </div>
    </div>
  );
});

ScrollView.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  scrollableStyle: PropTypes.shape({}),
  onScroll: PropTypes.func,
  children: PropTypes.node,
  resetScrollPosition: PropTypes.bool,
  propagateEventOnReachTop: PropTypes.bool,
  initialScrollPosition: PropTypes.number,
  initialMaxOffset: PropTypes.number,
  smoothScrollEnabled: PropTypes.bool,
  smoothScrollFactor: PropTypes.number,
};

export default memo(ScrollView);
